import React, { useState } from 'react';
import { Instagram, Send, Youtube, Twitter, ChevronDown, Mail, ArrowRight, CreditCard, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { useToast } from '../ui/Toast';
interface FooterProps {
  onNavigate: (page: string) => void;
}
export function Footer({
  onNavigate
}: FooterProps) {
  const {
    t
  } = useLanguage();
  const {
    success
  } = useToast();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      success('Подписка оформлена! Ждите наши письма.');
      setEmail('');
    }
  };
  const footerLinks = [{
    id: 'services',
    title: t('footer.services') || 'Услуги',
    links: [{
      label: t('footer.natal'),
      page: 'service-natal'
    }, {
      label: t('footer.solar'),
      page: 'service-solar'
    }, {
      label: t('footer.compatibility'),
      page: 'service-compatibility'
    }, {
      label: 'Комбо-анализ',
      page: 'service-combo'
    }, {
      label: 'Прогноз транзитов',
      page: 'service-forecast'
    }, {
      label: 'Консультация',
      page: 'service-consultation'
    }, {
      label: t('footer.pricingPackages'),
      page: 'pricing'
    }, {
      label: 'Маркетплейс',
      page: 'marketplace'
    }]
  }, {
    id: 'free',
    title: 'Бесплатное',
    links: [{
      label: 'Астро-календарь',
      page: 'astro-daily'
    }, {
      label: 'Бесплатная карта',
      page: 'free-chart'
    }, {
      label: 'Натальная карта онлайн',
      page: 'natal-demo'
    }, {
      label: 'Примеры отчётов',
      page: 'report-preview'
    }, {
      label: 'Premium отчёт',
      page: 'full-report'
    }]
  }, {
    id: 'knowledge',
    title: 'Знания',
    links: [{
      label: t('footer.interpretations'),
      page: 'interpretations'
    }, {
      label: t('footer.blog'),
      page: 'blog'
    }, {
      label: 'Вопросы и ответы',
      page: 'faq'
    }, {
      label: t('footer.astrologersCatalog'),
      page: 'astrologers'
    }]
  }, {
    id: 'company',
    title: 'Компания',
    links: [{
      label: 'О нас',
      page: 'about'
    }, {
      label: 'Контакты',
      page: 'contact'
    }, {
      label: t('footer.forAstrologers'),
      page: 'for-astrologers'
    }, {
      label: t('footer.advertising'),
      page: 'advertising'
    }]
  }];
  const socialLinks = [{
    icon: Instagram,
    href: 'https://instagram.com/astriqo',
    label: 'Instagram'
  }, {
    icon: Send,
    href: 'https://t.me/astriqo',
    label: 'Telegram'
  }, {
    icon: Youtube,
    href: 'https://youtube.com/@astriqo',
    label: 'YouTube'
  }, {
    icon: Twitter,
    href: 'https://twitter.com/astriqo',
    label: 'Twitter / X'
  }];
  return <footer className="bg-slate-950 text-slate-300 pt-16 pb-8 border-t border-slate-800 relative overflow-hidden" data-id="element-16228">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-indigo-600/10 blur-[120px] rounded-full pointer-events-none" data-id="element-16229" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-16230">
        {/* Top Section: Brand & Newsletter */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 pb-12 border-b border-slate-800/60" data-id="element-16231">
          <div className="lg:col-span-5" data-id="element-16232">
            <div className="flex items-center gap-3 mb-6" data-id="element-16233">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20" data-id="element-16234">
                <span className="text-white font-bold text-2xl" data-id="element-16235">A</span>
              </div>
              <span className="font-bold text-white text-2xl tracking-tight" data-id="element-16236">
                Astriqo
              </span>
            </div>
            <p className="text-slate-400 leading-relaxed mb-8 max-w-md" data-id="element-16237">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-4" data-id="element-16238">
              {socialLinks.map((social, idx) => {
              const Icon = social.icon;
              return <a key={idx} href={social.href} aria-label={social.label} className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 hover:border-slate-700 transition-all" data-id="element-16239">
                    <Icon className="w-4 h-4" data-id="element-16240" />
                  </a>;
            })}
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-12 flex flex-col justify-center" data-id="element-16241">
            <h3 className="text-white font-semibold text-lg mb-2" data-id="element-16242">
              Подпишитесь на астро-дайджест
            </h3>
            <p className="text-slate-400 text-sm mb-6" data-id="element-16243">
              Еженедельные прогнозы, полезные статьи и эксклюзивные скидки.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md" data-id="element-16244">
              <div className="relative flex-1" data-id="element-16245">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" data-id="element-16246" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Ваш email" required className="w-full pl-11 pr-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" data-id="element-16247" />
              </div>
              <button type="submit" className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors" data-id="element-16248">
                Подписаться
                <ArrowRight className="w-4 h-4" data-id="element-16249" />
              </button>
            </form>
          </div>
        </div>

        {/* Middle Section: Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-8 mb-16" data-id="element-16250">
          {footerLinks.map(section => <div key={section.id} className="border-b border-slate-800/60 md:border-none pb-4 md:pb-0" data-id="element-16251">
              {/* Mobile Accordion Header */}
              <button onClick={() => toggleSection(section.id)} className="flex items-center justify-between w-full md:cursor-default py-2 md:py-0 md:mb-6 group" data-id="element-16252">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider" data-id="element-16253">
                  {section.title}
                </h3>
                <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-300 md:hidden ${expandedSection === section.id ? 'rotate-180' : ''}`} data-id="element-16254" />
              </button>

              {/* Links List */}
              <ul className={`space-y-3 overflow-hidden transition-all duration-300 md:max-h-none md:opacity-100 ${expandedSection === section.id ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0 md:mt-0'}`} data-id="element-16255">
                {section.links.map((link, idx) => <li key={idx} data-id="element-16256">
                    <button onClick={() => onNavigate(link.page)} className="text-slate-400 hover:text-indigo-400 text-sm transition-colors flex items-center group" data-id="element-16257">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500/0 group-hover:bg-indigo-500 mr-2 transition-all" data-id="element-16258" />
                      {link.label}
                    </button>
                  </li>)}
              </ul>
            </div>)}
        </div>

        {/* Bottom Section: Legal & Payment */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 pt-8 border-t border-slate-800/60" data-id="element-16259">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 text-sm text-slate-500" data-id="element-16260">
            <p data-id="element-16261">
              {t('footer.copyright', {
              year: String(new Date().getFullYear())
            })}
            </p>
            <div className="flex items-center gap-6" data-id="element-16262">
              <button onClick={() => onNavigate('privacy')} className="hover:text-white transition-colors" data-id="element-16263">
                {t('footer.privacy')}
              </button>
              <button onClick={() => onNavigate('terms')} className="hover:text-white transition-colors" data-id="element-16264">
                {t('footer.terms')}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4 text-slate-500" data-id="element-16265">
            <div className="flex items-center gap-1.5 text-xs font-medium bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-800" data-id="element-16266">
              <ShieldCheck className="w-4 h-4 text-emerald-500" data-id="element-16267" />
              Безопасная оплата
            </div>
            <div className="flex items-center gap-2" data-id="element-16268">
              <div className="w-10 h-6 bg-slate-900 border border-slate-800 rounded flex items-center justify-center" data-id="element-16269">
                <CreditCard className="w-4 h-4" data-id="element-16270" />
              </div>
              <div className="w-10 h-6 bg-slate-900 border border-slate-800 rounded flex items-center justify-center font-bold text-[10px]" data-id="element-16271">
                MIR
              </div>
              <div className="w-10 h-6 bg-slate-900 border border-slate-800 rounded flex items-center justify-center font-bold text-[10px]" data-id="element-16272">
                СБП
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}